import { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import axios from 'axios';
import { Trash2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = "/api";

const Admin = () => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    location: '',
    capacity: '',
    image_url: '',
    completion_date: '',
    category: 'residential'
  });

  useEffect(() => {
    fetchContacts();
    fetchProjects();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject);
      toast.success('Project added successfully!');
      setShowAddProject(false);
      setNewProject({
        title: '',
        description: '',
        location: '',
        capacity: '',
        image_url: '',
        completion_date: '',
        category: 'residential'
      });
      fetchProjects();
    } catch (error) {
      console.error('Error adding project:', error);
      toast.error('Failed to add project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${API}/projects/${projectId}`);
        toast.success('Project deleted successfully!');
        fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <Navbar />

      <div className="pt-32 pb-20 min-h-screen bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-bold text-4xl text-primary mb-8">
            Admin Dashboard
          </h1>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 border-b border-border" data-testid="admin-tabs">
            <button
              onClick={() => setActiveTab('contacts')}
              data-testid="tab-contacts"
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'contacts'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-primary'
              }`}
            >
              Contact Submissions ({contacts.length})
              {activeTab === 'contacts' && (
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary"></span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('projects')}
              data-testid="tab-projects"
              className={`px-6 py-3 font-medium transition-colors relative ${
                activeTab === 'projects'
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-primary'
              }`}
            >
              Manage Projects ({projects.length})
              {activeTab === 'projects' && (
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-secondary"></span>
              )}
            </button>
          </div>

          {/* Contact Submissions */}
          {activeTab === 'contacts' && (
            <div className="space-y-4" data-testid="contacts-list">
              {contacts.length > 0 ? (
                contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="bg-card border border-border rounded-2xl p-6 shadow-sm"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Name</div>
                        <div className="font-semibold">{contact.name}</div>
                      </div>

                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Email</div>
                        <div className="font-semibold">{contact.email}</div>
                      </div>

                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Phone</div>
                        <div className="font-semibold">{contact.phone}</div>
                      </div>

                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Date</div>
                        <div className="font-semibold">
                          {new Date(contact.timestamp).toLocaleString()}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <div className="text-sm text-muted-foreground mb-1">Message</div>
                        <div className="text-foreground leading-relaxed">
                          {contact.message}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-20 text-muted-foreground">
                  No contact submissions yet.
                </div>
              )}
            </div>
          )}

          {/* Projects Management */}
          {activeTab === 'projects' && (
            <div data-testid="projects-management">
              <div className="mb-6">
                <button
                  onClick={() => setShowAddProject(true)}
                  data-testid="add-project-button"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-6 py-3 font-medium transition-all hover:scale-105 shadow-lg inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" /> Add New Project
                </button>
              </div>

              {/* Add Project Modal */}
              {showAddProject && (
                <div
                  className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                  data-testid="add-project-modal"
                >
                  <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="font-heading font-semibold text-2xl">Add New Project</h2>
                      <button
                        onClick={() => setShowAddProject(false)}
                        className="p-2 hover:bg-muted rounded-lg"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>

                    <form onSubmit={handleAddProject} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Title *</label>
                        <input
                          type="text"
                          required
                          value={newProject.title}
                          onChange={(e) =>
                            setNewProject({ ...newProject, title: e.target.value })
                          }
                          className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Description *</label>
                        <textarea
                          required
                          rows="3"
                          value={newProject.description}
                          onChange={(e) =>
                            setNewProject({ ...newProject, description: e.target.value })
                          }
                          className="w-full bg-muted/30 border-border rounded-xl px-4 py-3 resize-none"
                        ></textarea>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Location *</label>
                          <input
                            type="text"
                            required
                            value={newProject.location}
                            onChange={(e) =>
                              setNewProject({ ...newProject, location: e.target.value })
                            }
                            className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Capacity *</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g., 10 kW"
                            value={newProject.capacity}
                            onChange={(e) =>
                              setNewProject({ ...newProject, capacity: e.target.value })
                            }
                            className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Image URL *</label>
                        <input
                          type="url"
                          required
                          value={newProject.image_url}
                          onChange={(e) =>
                            setNewProject({ ...newProject, image_url: e.target.value })
                          }
                          className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Completion Date *
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="e.g., Jan 2024"
                            value={newProject.completion_date}
                            onChange={(e) =>
                              setNewProject({
                                ...newProject,
                                completion_date: e.target.value
                              })
                            }
                            className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">Category *</label>
                          <select
                            value={newProject.category}
                            onChange={(e) =>
                              setNewProject({ ...newProject, category: e.target.value })
                            }
                            className="w-full bg-muted/30 border-border rounded-xl h-12 px-4"
                          >
                            <option value="residential">Residential</option>
                            <option value="commercial">Commercial</option>
                            <option value="industrial">Industrial</option>
                          </select>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-3 font-medium transition-all"
                      >
                        Add Project
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Projects List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm"
                  >
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <h3 className="font-heading font-semibold text-lg mb-2">
                        {project.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {project.description}
                      </p>

                      <div className="flex justify-between items-center">
                        <span className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full capitalize">
                          {project.category}
                        </span>

                        <button
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {projects.length === 0 && (
                <div className="text-center py-20 text-muted-foreground">
                  No projects found.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Admin;
